// src/components/GeminiOCREnhancer.js
import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Loader, Brain, Zap } from 'lucide-react';

// 🔒 医療機関向けセキュアAIサービス
class GeminiOCREnhancer {
  constructor() {
    this.apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    this.model = null;
    this.isInitialized = false;
    
    // 🛡️ データ保護フィルター
    this.sensitivePatterns = [
      // 患者個人情報
      /患者.*?[名前|氏名|ID|番号]/gi,
      /[A-Z][a-z]+\s+[A-Z][a-z]+/g, // 人名パターン
      /\d{4}年\d{1,2}月\d{1,2}日/g, // 具体的日付
      /\d{3}-\d{4}-\d{4}/g, // 電話番号
      
      // 具体的な医療情報
      /手術室\s*[A-Z0-9]+/gi,
      /病床\s*[0-9]+/gi,
      /カルテ\s*[番号|No|ID]/gi,
      
      // スタッフ個人情報
      /[田中|佐藤|鈴木|高橋|渡辺|伊藤|山本|中村|小林|加藤]/g, // 一般的な姓
      /Dr\.|医師|看護師.*?[名前|氏名]/gi,
      
      // 具体的数値データ
      /在庫.*?\d+個/gi,
      /場所.*?[A-Z]\-\d+/gi,
    ];
  }

  async initialize() {
    if (this.isInitialized) return true;
    
    try {
      const { GoogleGenerativeAI } = await import("@google/generative-ai");
      const genAI = new GoogleGenerativeAI(this.apiKey);
      
      this.model = genAI.getGenerativeModel({ 
        model: "gemini-1.5-flash",
        generationConfig: {
          temperature: 0.1, // 医療データなので低温度で正確性を重視
          topP: 0.8,
          topK: 40,
          maxOutputTokens: 2000,
        }
      });
      
      this.isInitialized = true;
      console.log('🧠 Gemini OCR Enhancer initialized');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Gemini OCR Enhancer:', error);
      return false;
    }
  }

  // 🛡️ 機密データフィルタリング
  filterSensitiveData(text) {
    let filteredText = text;
    let hasFilteredContent = false;

    // センシティブパターンをマスク
    this.sensitivePatterns.forEach(pattern => {
      if (pattern.test(filteredText)) {
        hasFilteredContent = true;
        filteredText = filteredText.replace(pattern, '[機密情報-除去済み]');
      }
    });

    // 数字の連続を部分的にマスク
    filteredText = filteredText.replace(/\d{4,}/g, (match) => {
      hasFilteredContent = true;
      return '[数値データ-保護済み]';
    });

    return {
      filteredText,
      hasFilteredContent,
      originalLength: text.length,
      filteredLength: filteredText.length
    };
  }

  // 📋 手術スケジュール表専用のプロンプト
  buildSurgerySchedulePrompt(ocrResult, imageContext = {}) {
    return `
あなたは医療機関の手術スケジュール表を専門に扱うAIアシスタントです。

【重要なタスク】
OCR（光学文字認識）で読み取った手術スケジュール表のデータを分析・修正して、正確な構造化データを生成してください。

【OCR元データ】
${JSON.stringify(ocrResult, null, 2)}

【期待する出力形式】
必ず以下のJSON形式で回答してください：

{
  "corrected_table": {
    "headers": ["日付", "手術室", "開始時間", "診療科", "術式", "執刀医"],
    "rows": [
      ["2024-01-15", "Op01", "09:00", "外科", "腹腔鏡下胆嚢摘出術", "田中医師"],
      ["2024-01-15", "Op02", "13:00", "整形外科", "人工膝関節置換術", "佐藤医師"]
    ]
  },
  "column_mapping": {
    "date": 0,
    "room": 1, 
    "start": 2,
    "department": 3,
    "procedure": 4,
    "surgeon": 5
  },
  "data_quality": {
    "confidence": 95,
    "corrections_made": [
      "「手術空」→「手術室」に修正",
      "時刻フォーマットを「HH:MM」に統一"
    ],
    "issues_found": [
      "3行目の執刀医名が不明瞭"
    ]
  },
  "extracted_surgeries": [
    {
      "date": "2024-01-15",
      "room": "Op01", 
      "start": "09:00",
      "end": "10:00",
      "department": "外科",
      "procedure": "腹腔鏡下胆嚢摘出術",
      "surgeon": "田中医師",
      "priority": "normal",
      "position": "",
      "anesthesia": ""
    }
  ]
}

【修正ルール】
1. 医療用語の正規化（例：「手術空」→「手術室」）
2. 時間フォーマットの統一（「HH:MM」形式）
3. 手術室名の標準化（例：「OR1」→「Op01」）
4. 日付フォーマットの統一（「YYYY-MM-DD」形式）
5. 不明瞭な文字の医学的文脈からの推測
6. 欠損データの合理的補完

【注意事項】
- 患者・スタッフの個人情報は一切含めない
- 不確実な情報は confidence を下げて記録
- 医療安全のため、不明な場合は null とする
- 標準的な医療用語を使用する

JSON形式で回答してください：`;
  }

  // 🔍 OCR結果をGeminiで分析・修正
  async enhanceOCRResults(ocrResult, imageContext = {}) {
    if (!this.isInitialized) {
      const initialized = await this.initialize();
      if (!initialized) {
        throw new Error('Gemini OCR Enhancer initialization failed');
      }
    }

    try {
      console.log('🧠 Gemini analyzing OCR results...');
      
      const prompt = this.buildSurgerySchedulePrompt(ocrResult, imageContext);
      const result = await this.model.generateContent(prompt);
      const response = await result.response;
      const responseText = response.text();

      // JSONレスポンスを抽出
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('Gemini response format error: No JSON found');
      }

      const enhancedData = JSON.parse(jsonMatch[0]);
      
      console.log('✅ Gemini OCR enhancement completed');
      
      return {
        success: true,
        enhanced_data: enhancedData,
        confidence: enhancedData.data_quality?.confidence || 0,
        corrections_made: enhancedData.data_quality?.corrections_made || [],
        issues_found: enhancedData.data_quality?.issues_found || [],
        processing_time: new Date(),
        source: 'Gemini AI Enhanced'
      };

    } catch (error) {
      console.error('❌ Gemini OCR Enhancement Error:', error);
      
      // フォールバック: 元のOCRデータを返す
      return {
        success: false,
        error: error.message,
        enhanced_data: null,
        confidence: 0,
        fallback_data: ocrResult,
        source: 'OCR Only (Gemini Failed)'
      };
    }
  }
}

// Gemini OCR Enhancerのインスタンス
const geminiOCREnhancer = new GeminiOCREnhancer();

// 🎯 既存のOCRコンポーネントに統合するための拡張コンポーネント
const GeminiEnhancedOCRProcessor = ({ originalOCRResult, onEnhancedResult, imageFilters = [] }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [enhancementResult, setEnhancementResult] = useState(null);
  const [processingSteps, setProcessingSteps] = useState([]);

  const addProcessingStep = (step) => {
    setProcessingSteps(prev => [...prev, { ...step, timestamp: new Date() }]);
  };

  const handleGeminiEnhancement = async () => {
    setIsProcessing(true);
    setProcessingSteps([]);
    
    try {
      addProcessingStep({ status: 'start', message: 'Gemini AI分析開始' });
      
      // 1. 基本的なOCR結果の分析・修正
      const enhancedResult = await geminiOCREnhancer.enhanceOCRResults(originalOCRResult);
      
      if (enhancedResult.success) {
        addProcessingStep({ 
          status: 'success', 
          message: `OCR精度向上完了 (信頼度: ${enhancedResult.confidence}%)` 
        });
        
        setEnhancementResult(enhancedResult);
        
        // 親コンポーネントに結果を通知
        onEnhancedResult({
          originalOCR: originalOCRResult,
          geminiEnhanced: enhancedResult.enhanced_data,
          confidence: enhancedResult.confidence
        });
        
      } else {
        addProcessingStep({ 
          status: 'error', 
          message: `Gemini処理失敗: ${enhancedResult.error}` 
        });
      }
      
    } catch (error) {
      console.error('❌ Gemini enhancement error:', error);
      addProcessingStep({ status: 'error', message: `エラー: ${error.message}` });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white border border-blue-200 rounded-lg p-6 mt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Brain className="w-6 h-6 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Gemini AI 精度向上</h3>
        </div>
        
        {!isProcessing && !enhancementResult && (
          <button
            onClick={handleGeminiEnhancement}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Zap className="w-4 h-4 mr-2" />
            AI精度向上実行
          </button>
        )}
      </div>

      {/* 処理ステップ表示 */}
      {processingSteps.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">処理ステップ:</h4>
          <div className="space-y-2">
            {processingSteps.map((step, index) => (
              <div key={index} className="flex items-center text-sm">
                {step.status === 'start' && <Loader className="w-4 h-4 text-blue-500 mr-2 animate-spin" />}
                {step.status === 'processing' && <Loader className="w-4 h-4 text-yellow-500 mr-2 animate-spin" />}
                {step.status === 'success' && <CheckCircle className="w-4 h-4 text-green-500 mr-2" />}
                {step.status === 'warning' && <AlertCircle className="w-4 h-4 text-yellow-500 mr-2" />}
                {step.status === 'error' && <AlertCircle className="w-4 h-4 text-red-500 mr-2" />}
                <span className={
                  step.status === 'success' ? 'text-green-700' :
                  step.status === 'warning' ? 'text-yellow-700' :
                  step.status === 'error' ? 'text-red-700' : 'text-gray-700'
                }>
                  {step.message}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 拡張結果表示 */}
      {enhancementResult && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-2">🧠 Gemini AI 分析結果</h4>
            <div className="text-sm text-green-700 space-y-1">
              <p>信頼度: {enhancementResult.confidence}%</p>
              
              {enhancementResult.corrections_made?.length > 0 && (
                <div>
                  <p className="font-medium">修正項目:</p>
                  <ul className="list-disc list-inside ml-2">
                    {enhancementResult.corrections_made.map((correction, index) => (
                      <li key={index}>{correction}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {enhancementResult.issues_found?.length > 0 && (
                <div>
                  <p className="font-medium">検出された問題:</p>
                  <ul className="list-disc list-inside ml-2">
                    {enhancementResult.issues_found.map((issue, index) => (
                      <li key={index} className="text-yellow-700">{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isProcessing && (
        <div className="flex items-center justify-center py-6">
          <Loader className="animate-spin h-6 w-6 text-blue-600 mr-3" />
          <span className="text-blue-700 font-medium">Gemini AI で分析中...</span>
        </div>
      )}
    </div>
  );
};

export { GeminiOCREnhancer, GeminiEnhancedOCRProcessor };